import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Divider,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import userService from '../services/userService';
import licenseComplianceService from '../services/licenseComplianceService';
import type {
  ComplianceSetupStateDto,
  LicenseSetupEntryDto,
} from '../services/licenseComplianceService';
import { getApiErrorInfo } from '../services/api';
import { track } from '../services/analytics';
import { useUserContext } from '../hooks/useUserContext';
import { US_STATES } from '../components/profile/profileConstants';
import LicenseRequirementCard from '../components/compliance/LicenseRequirementCard';
import { hideHoursBasedEthics } from '../components/compliance/ethicsDisplay';
import ProvenanceBadge from '../components/compliance/ProvenanceBadge';
import PrimaryLicenseChip from '../components/compliance/PrimaryLicenseChip';
import ConfirmDialog from '../components/common/ConfirmDialog';

const STEPS = ['Add your licenses', 'Confirm CPE requirements', 'Review & finish'];

/** Survives a mid-wizard refresh; cleared when onboarding completes or is skipped. */
const STEP_STORAGE_KEY = 'onboarding.activeStep';

function restoreStep(): number {
  const saved = Number(sessionStorage.getItem(STEP_STORAGE_KEY));
  return Number.isInteger(saved) && saved > 0 && saved < STEPS.length ? saved : 0;
}

/**
 * Guided onboarding wizard (/app/onboarding): add CPA licenses, confirm the
 * CPE requirements tracked for each one, then review. Reuses the same
 * requirement cards and endpoints as License Management, so anything set up
 * here can be edited later on /app/licenses.
 */
/** Location captured by OnboardingGate so skip can resume the interrupted navigation. */
type ResumeTo = { pathname: string; state?: unknown };

export default function Onboarding() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshUser } = useUserContext();
  const resumeTo = (location.state as { resumeTo?: ResumeTo } | null)?.resumeTo;

  const [setupState, setSetupState] = useState<ComplianceSetupStateDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [activeStep, setActiveStep] = useState(restoreStep);

  useEffect(() => {
    sessionStorage.setItem(STEP_STORAGE_KEY, String(activeStep));
  }, [activeStep]);

  // Add-license form
  const [stateCode, setStateCode] = useState('');
  const [licenseNumber, setLicenseNumber] = useState('');
  const [issueDate, setIssueDate] = useState('');
  const [adding, setAdding] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [licenseToRemove, setLicenseToRemove] = useState<LicenseSetupEntryDto | null>(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);
  // Which finish-the-wizard action is in flight ('skip' | 'finish'), so only
  // the clicked button shows a busy label while both stay disabled.
  const [completing, setCompleting] = useState<'skip' | 'finish' | null>(null);
  // Step-2 primary action busy state: confirming remaining requirements before advancing.
  const [confirming, setConfirming] = useState(false);

  const loadData = useCallback(async () => {
    setLoadError(null);
    try {
      const setup = await licenseComplianceService.getSetupState();
      setSetupState(setup);
      // Clamp a step restored from sessionStorage that the data no longer
      // supports (e.g. licenses were removed on License Management in another tab).
      setActiveStep((step) => {
        if (setup.licenses.length === 0) return 0;
        const done = setup.licenses.every(
          (l) => l.status === 'confirmed' || l.status === 'modified'
        );
        return step === 2 && !done ? 1 : step;
      });
    } catch (error) {
      setLoadError(getApiErrorInfo(error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const licenses = setupState?.licenses ?? [];
  // Same "set up" definition as licenseStatus.ts: a requirement row that was
  // confirmed as-is or saved with custom values counts as done.
  const confirmedCount = licenses.filter(
    (l) => l.status === 'confirmed' || l.status === 'modified'
  ).length;
  const allSetUp = licenses.length > 0 && confirmedCount === licenses.length;
  // Licenses with no requirement row have nothing to confirm yet ("Load state
  // requirements" or manual entry must happen first), so they block the
  // combined confirm-and-continue action on the requirements step.
  const missingSetup = licenses.filter((l) => !l.requirement);
  const showSetupHelper = activeStep === 1 && missingSetup.length > 0;
  const availableStates = US_STATES.filter(
    (s) => !licenses.some((l) => l.stateCode === s.code)
  );

  const handleAddLicense = async () => {
    if (!stateCode) return;
    setAdding(true);
    setActionError(null);
    let createdId: string;
    try {
      const created = await userService.createLicense({
        stateCode,
        licenseNumber: licenseNumber || null,
        issueDate: issueDate || null,
        isActive: true,
        isPrimary: licenses.length === 0, // First license is primary by default
      });
      createdId = created.id;
    } catch (error) {
      setActionError(getApiErrorInfo(error).message);
      setAdding(false);
      return;
    }
    // The license exists server-side from here on; clear the form and keep the
    // list in sync even if the follow-up requests below fail.
    setStateCode('');
    setLicenseNumber('');
    setIssueDate('');
    try {
      // Prefill the new license's requirements from state board data when available
      let next = await licenseComplianceService.getSetupState();
      const entry = next.licenses.find((l) => l.licenseId === createdId);
      if (entry?.hasStateData && entry.status === 'not_set_up') {
        try {
          next = await licenseComplianceService.prefillLicense(createdId);
        } catch {
          // Non-fatal: the requirement card offers "Load state requirements" as a retry.
        }
      }
      setSetupState(next);
    } catch (error) {
      setActionError(
        `Your license was added, but the list couldn't refresh: ${getApiErrorInfo(error).message}`
      );
    } finally {
      setAdding(false);
    }
  };

  /**
   * Step-2 primary action: confirm every still-unconfirmed license with its
   * loaded values (same call the card's "Confirm requirements" makes, passing
   * the original timestamps so the server keeps its exact prefill match), then
   * advance to review. One click both confirms and continues, so nobody stalls
   * on a disabled Continue wondering what's left to do.
   */
  const handleConfirmAndContinue = async () => {
    const pending = licenses
      .filter((l) => l.status === 'unconfirmed')
      .flatMap((l) => (l.requirement ? [{ licenseId: l.licenseId, requirement: l.requirement }] : []));
    if (pending.length === 0) {
      setActiveStep(2);
      return;
    }
    setConfirming(true);
    setActionError(null);
    try {
      await Promise.all(
        pending.map(({ licenseId, requirement }) =>
          licenseComplianceService.updateRequirement(licenseId, {
            totalHours: requirement.totalHours,
            ethicsHours: requirement.ethicsHours,
            periodStart: requirement.periodStart,
            periodEnd: requirement.periodEnd,
            confirm: true,
          })
        )
      );
      setSetupState(await licenseComplianceService.getSetupState());
      setActiveStep(2);
    } catch (error) {
      setActionError(getApiErrorInfo(error).message);
      // Some licenses may have confirmed before the failure; refresh so the
      // cards and badges reflect what actually landed.
      await loadData();
    } finally {
      setConfirming(false);
    }
  };

  /** Persist the flag, then refresh the profile so OnboardingGate stops redirecting. */
  const completeOnboarding = async () => {
    await userService.markOnboardingCompleted();
    await refreshUser();
  };

  const handleSkip = async () => {
    setCompleting('skip');
    setActionError(null);
    try {
      await completeOnboarding();
      sessionStorage.removeItem(STEP_STORAGE_KEY);
      track('onboarding_skipped', { step: activeStep });
      // Resume the navigation the gate interrupted (preserves router state such
      // as the assistant's planData); fall back to the app home.
      navigate(resumeTo?.pathname ?? '/app/assistant', { state: resumeTo?.state, replace: true });
    } catch (error) {
      setActionError(getApiErrorInfo(error).message);
      setCompleting(null);
    }
  };

  const handleFinish = async () => {
    setCompleting('finish');
    setActionError(null);
    try {
      await completeOnboarding();
      sessionStorage.removeItem(STEP_STORAGE_KEY);
      track('onboarding_completed', { license_count: licenses.length });
      navigate('/app/cpe-history', { replace: true });
    } catch (error) {
      setActionError(getApiErrorInfo(error).message);
      setCompleting(null);
    }
  };

  const handleRemoveLicense = async () => {
    if (!licenseToRemove) return;
    setRemoving(true);
    setRemoveError(null);
    try {
      await userService.deleteLicense(licenseToRemove.licenseId);
      const next = await licenseComplianceService.getSetupState();
      setSetupState(next);
      setLicenseToRemove(null);
    } catch (error) {
      setRemoveError(getApiErrorInfo(error).message);
    } finally {
      setRemoving(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 780, mx: 'auto' }}>
      <Typography variant="h4" component="h1" align="center" sx={{ fontWeight: 700, mb: 0.5 }}>
        Set up your CPE tracking
      </Typography>
      <Typography align="center" color="text.secondary" sx={{ mb: 4 }}>
        Tell us where you're licensed and we'll load each state's requirements for you to
        review and confirm.
      </Typography>

      <Stepper
        activeStep={activeStep}
        alternativeLabel
        sx={{ mb: 4, '& .MuiStepLabel-label': { fontSize: { xs: '0.75rem', sm: '0.875rem' } } }}
      >
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {loading ? (
        <Stack gap={1.5}>
          <Skeleton variant="rounded" height={56} />
          <Skeleton variant="rounded" height={200} />
        </Stack>
      ) : loadError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => { setLoading(true); void loadData(); }}>
              Retry
            </Button>
          }
        >
          {loadError}
        </Alert>
      ) : (
        <Paper sx={{ p: { xs: 2, md: 4 } }}>
          {activeStep === 0 && (
            <>
              <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
                Where do you hold a CPA license?
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} sx={{ mb: 2 }}>
                <Autocomplete
                  options={availableStates}
                  getOptionLabel={(s) => s.name}
                  value={US_STATES.find((s) => s.code === stateCode) ?? null}
                  onChange={(_, value) => setStateCode(value?.code ?? '')}
                  size="small"
                  sx={{ minWidth: 200 }}
                  renderInput={(params) => <TextField {...params} label="State" />}
                />
                <TextField
                  label="License number (optional)"
                  size="small"
                  value={licenseNumber}
                  onChange={(e) => setLicenseNumber(e.target.value)}
                />
                <TextField
                  label="Issue date (optional)"
                  type="date"
                  size="small"
                  value={issueDate}
                  onChange={(e) => setIssueDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <Button variant="contained" onClick={() => void handleAddLicense()} disabled={!stateCode || adding}>
                  {adding ? 'Adding…' : 'Add license'}
                </Button>
              </Stack>
              {licenses.length > 0 && <Divider sx={{ my: 2 }} />}
              <Stack gap={1}>
                {licenses.map((l) => (
                  <Stack
                    key={l.licenseId}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ px: 1.5, py: 1, borderRadius: 1, bgcolor: 'tint.primarySoft' }}
                  >
                    <Stack direction="row" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {l.stateName ?? l.stateCode}
                        {l.licenseNumber && (
                          <Typography component="span" variant="body2" color="text.secondary">
                            {' '}
                            · #{l.licenseNumber}
                          </Typography>
                        )}
                      </Typography>
                      {l.isPrimary && <PrimaryLicenseChip />}
                      <ProvenanceBadge status={l.status} stateCode={l.stateCode} />
                    </Stack>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setRemoveError(null);
                        setLicenseToRemove(l);
                      }}
                      aria-label={`Remove ${l.stateName ?? l.stateCode} license`}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>
            </>
          )}

          {activeStep === 1 && (
            <Stack gap={2}>
              <Typography variant="h6" component="h2">
                Confirm what each state requires
              </Typography>
              <Typography variant="body2" color="text.secondary">
                We loaded requirements from each state board where data is available. Review and
                confirm them, or adjust anything that doesn't match your situation.
              </Typography>
              {licenses.map((l) => {
                const primary = licenses.find((x) => x.isPrimary);
                return (
                  <LicenseRequirementCard
                    key={l.licenseId}
                    entry={l}
                    onSaved={loadData}
                    primaryStateLabel={primary ? primary.stateName || primary.stateCode : null}
                  />
                );
              })}
            </Stack>
          )}

          {activeStep === 2 && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <CheckCircleOutlineIcon sx={{ fontSize: 56, color: 'status.completed.dot', mb: 1 }} />
              <Typography variant="h5" component="h2" sx={{ fontWeight: 700, mb: 1 }}>
                You're all set
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                We'll track your progress against these requirements as you complete courses.
              </Typography>
              <Stack gap={1} sx={{ maxWidth: 420, mx: 'auto', mb: 3, textAlign: 'left' }}>
                {licenses.map((l) => (
                  <Stack
                    key={l.licenseId}
                    direction="row"
                    justifyContent="space-between"
                    sx={{ px: 2, py: 1, borderRadius: 1, bgcolor: 'surface.subtle' }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {l.stateName ?? l.stateCode}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {l.requirement
                        ? hideHoursBasedEthics(l.requirement.ethicsHours, l.subjectRequirements)
                          ? `${l.requirement.totalHours} hrs`
                          : `${l.requirement.totalHours} hrs · ${l.requirement.ethicsHours} ethics`
                        : 'Not set up'}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
              <Button
                variant="contained"
                size="large"
                disabled={completing !== null}
                onClick={() => void handleFinish()}
              >
                {completing === 'finish' ? 'Finishing…' : 'View my reporting'}
              </Button>
            </Box>
          )}

          {actionError && (
            <Alert severity="error" sx={{ mt: 3 }} onClose={() => setActionError(null)}>
              {actionError}
            </Alert>
          )}
          {showSetupHelper && (
            <Typography
              variant="caption"
              color="text.secondary"
              align="right"
              sx={{ display: 'block', mt: 3 }}
            >
              Set up requirements for{' '}
              {missingSetup.map((l) => l.stateName ?? l.stateCode).join(', ')} above to continue.
            </Typography>
          )}
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: showSetupHelper ? 1 : 4 }}>
            <Button
              disabled={activeStep === 0 || completing !== null || confirming}
              onClick={() => setActiveStep((s) => s - 1)}
            >
              Back
            </Button>
            {activeStep < 2 && (
              <Stack direction="row" alignItems="center" gap={1.5}>
                <Button
                  size="small"
                  disabled={completing !== null || confirming}
                  onClick={() => void handleSkip()}
                  sx={{ color: 'text.secondary' }}
                >
                  {completing === 'skip' ? 'Skipping…' : 'Skip for now'}
                </Button>
                <Button
                  variant="contained"
                  disabled={
                    completing !== null ||
                    confirming ||
                    (activeStep === 0 && licenses.length === 0) ||
                    (activeStep === 1 && missingSetup.length > 0)
                  }
                  onClick={() => {
                    if (activeStep === 1) void handleConfirmAndContinue();
                    else setActiveStep((s) => s + 1);
                  }}
                >
                  {activeStep === 1
                    ? confirming
                      ? 'Confirming…'
                      : allSetUp
                        ? 'Continue'
                        : 'Confirm requirements and continue'
                    : 'Continue'}
                </Button>
              </Stack>
            )}
          </Stack>
          {activeStep < 2 && (
            <Typography
              variant="caption"
              color="text.secondary"
              align="right"
              sx={{ display: 'block', mt: 1 }}
            >
              If you skip, you can finish setup anytime on the License Management page.
            </Typography>
          )}
        </Paper>
      )}

      <ConfirmDialog
        open={licenseToRemove !== null}
        title="Remove license?"
        message={`Your ${
          licenseToRemove?.stateName ?? licenseToRemove?.stateCode ?? ''
        } license and any requirements set up for it will no longer be tracked.`}
        confirmLabel="Remove"
        loading={removing}
        error={removeError}
        onConfirm={() => void handleRemoveLicense()}
        onClose={() => setLicenseToRemove(null)}
      />
    </Box>
  );
}
